/**
 * Enhanced Map Visualization using Mapbox GL
 * Converts analysis results into interactive map visualizations
 */

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import Map, { Source, Layer, Marker, Popup, NavigationControl, ScaleControl, MapRef } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';
import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormGroup,
  Divider,
  Card,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Mouse as MouseIcon,
} from '@mui/icons-material';
import MapStyleControl, { MapStyleType } from '../cop/MapStyleControl';

import { DwellLocation, Route } from '../../types';

// Helper: ensure a value is a valid latitude in [-90, 90]
const isValidLat = (v: unknown): v is number =>
  typeof v === 'number' && Number.isFinite(v) && v >= -90 && v <= 90;
// Helper: ensure a value is a valid longitude in [-180, 180]
const isValidLng = (v: unknown): v is number =>
  typeof v === 'number' && Number.isFinite(v) && v >= -180 && v <= 180;
// Only call fitBounds when bounds are valid for Mapbox
const areBoundsValid = (
  minLng: number,
  minLat: number,
  maxLng: number,
  maxLat: number
): boolean =>
  isValidLng(minLng) && isValidLat(minLat) && isValidLng(maxLng) && isValidLat(maxLat);

interface EnhancedMapVisualizationGLProps {
  dwellLocations: DwellLocation[];
  routes: Route[];
  heatmapData?: Array<{
    lat: number;
    lon: number;
    intensity: number;
    timestamp?: string;
    hour?: number;
    is_daytime?: boolean;
    entity_id?: string;
  }>;
  daytimeHeatmapData?: Array<{
    lat: number;
    lon: number;
    intensity: number;
  }>;
  nighttimeHeatmapData?: Array<{
    lat: number;
    lon: number;
    intensity: number;
  }>;
  deviceTracks?: Record<string, {
    coordinates: [number, number][];
    point_count: number;
    device_type: string;
  }>;
  temporalBreakdown?: {
    daytime_points: number;
    nighttime_points: number;
    total_points: number;
  };
  activeTab?: number;
  selectedDwellId?: number | null;
  selectedRouteId?: number | null;
}

interface LayerState {
  dwellLocations: boolean;
  highFreqRoutes: boolean;
  medFreqRoutes: boolean;
  lowFreqRoutes: boolean;
  allDeviceTracks: boolean;
  heatmap: boolean;
  daytimeActivity: boolean;
  nighttimeActivity: boolean;
}

const EnhancedMapVisualizationGL: React.FC<EnhancedMapVisualizationGLProps> = ({
  dwellLocations,
  routes,
  heatmapData = [],
  daytimeHeatmapData = [],
  nighttimeHeatmapData = [],
  deviceTracks = {},
  temporalBreakdown,
  activeTab,
  selectedDwellId,
  selectedRouteId,
}) => {
  const [mapStyle, setMapStyle] = useState<MapStyleType>('dark');
  const [scrollWheelZoom, setScrollWheelZoom] = useState<boolean>(false);
  const [selectedDwell, setSelectedDwell] = useState<DwellLocation | null>(null);
  const mapRef = useRef<MapRef | null>(null);
  const geocoderRef = useRef<MapboxGeocoder | null>(null);

  // Define layer presets for each tab
  const getLayerPresets = (tabIndex?: number): LayerState => {
    switch (tabIndex) {
      case 0: // Overview
        return {
          dwellLocations: true,
          highFreqRoutes: false,
          medFreqRoutes: false,
          lowFreqRoutes: false,
          allDeviceTracks: true,
          heatmap: true,
          daytimeActivity: false,
          nighttimeActivity: false,
        };
      case 1: // Dwell Analysis
        return {
          dwellLocations: true,
          highFreqRoutes: false,
          medFreqRoutes: false,
          lowFreqRoutes: false,
          allDeviceTracks: false,
          heatmap: false,
          daytimeActivity: false,
          nighttimeActivity: false,
        };
      case 2: // Route Patterns
        return {
          dwellLocations: true,
          highFreqRoutes: true,
          medFreqRoutes: true,
          lowFreqRoutes: true,
          allDeviceTracks: false,
          heatmap: false,
          daytimeActivity: false,
          nighttimeActivity: false,
        };
      case 3: // Day vs Night Activity
        return {
          dwellLocations: true,
          highFreqRoutes: false,
          medFreqRoutes: false,
          lowFreqRoutes: false,
          allDeviceTracks: false,
          heatmap: false,
          daytimeActivity: true,
          nighttimeActivity: true,
        };
      default:
        return {
          dwellLocations: true,
          highFreqRoutes: false,
          medFreqRoutes: false,
          lowFreqRoutes: false,
          allDeviceTracks: true,
          heatmap: true,
          daytimeActivity: false,
          nighttimeActivity: false,
        };
    }
  };

  const [layers, setLayers] = useState<LayerState>(getLayerPresets(activeTab));
  const [mapLoaded, setMapLoaded] = useState(false);
  const [hasFitBounds, setHasFitBounds] = useState(false);
  
  // Calculate initial viewport from data if available, otherwise use a neutral center
  const initialViewport = useMemo(() => {
    if (dwellLocations.length > 0) {
      const lons = dwellLocations.map(d => d.center_longitude);
      const lats = dwellLocations.map(d => d.center_latitude);
      return {
        longitude: (Math.min(...lons) + Math.max(...lons)) / 2,
        latitude: (Math.min(...lats) + Math.max(...lats)) / 2,
        zoom: 10
      };
    }
    // Neutral center (US center) - will be overridden by fitBounds
    return { longitude: -98.5795, latitude: 39.8283, zoom: 4 };
  }, [dwellLocations]);
  
  const [viewport, setViewport] = useState(initialViewport);

  // Update layers when activeTab changes
  useEffect(() => {
    if (activeTab !== undefined) {
      setLayers(getLayerPresets(activeTab));
    }
  }, [activeTab]);

  // Filter data based on selected table rows
  const filteredDwellLocations = selectedDwellId
    ? dwellLocations.filter(dwell => dwell.id === selectedDwellId)
    : dwellLocations;

  const filteredRoutes = selectedRouteId
    ? routes.filter(route => route.id === selectedRouteId)
    : routes;

  // Fit map to data bounds - runs when map loads or when data changes
  const fitMapToBounds = useCallback(() => {
    if (!mapRef.current) return;

    const allPoints: [number, number][] = [];

  // Use all dwell locations (not filtered) for initial fit - only valid coords
  dwellLocations.forEach(location => {
    const lng = location.center_longitude;
    const lat = location.center_latitude;
    if (isValidLng(lng) && isValidLat(lat)) {
      allPoints.push([lng, lat]);
    }
  });

  // Include route coordinates
  routes.forEach(route => {
    if (route.path_coordinates && Array.isArray(route.path_coordinates)) {
      route.path_coordinates.forEach(coord => {
        if (coord && coord.length >= 2) {
          const a = Number(coord[0]);
          const b = Number(coord[1]);
          if (isValidLng(a) && isValidLat(b)) {
            allPoints.push([a, b]);
          } else if (isValidLng(b) && isValidLat(a)) {
            allPoints.push([b, a]);
          }
        }
      });
    }
  });

  // Include heatmap data points (GPS data)
  heatmapData.forEach(point => {
    const lon = point.lon;
    const lat = point.lat;
    if (isValidLng(lon) && isValidLat(lat)) {
      allPoints.push([lon, lat]);
    }
  });

  // Include daytime heatmap data
  daytimeHeatmapData.forEach(point => {
    const lon = point.lon;
    const lat = point.lat;
    if (isValidLng(lon) && isValidLat(lat)) {
      allPoints.push([lon, lat]);
    }
  });

  // Include nighttime heatmap data
  nighttimeHeatmapData.forEach(point => {
    const lon = point.lon;
    const lat = point.lat;
    if (isValidLng(lon) && isValidLat(lat)) {
      allPoints.push([lon, lat]);
    }
  });

  // Include device track coordinates
  Object.values(deviceTracks).forEach(track => {
    if (track.coordinates && Array.isArray(track.coordinates)) {
      track.coordinates.forEach(coord => {
        if (coord && coord.length >= 2) {
          const lng = Number(coord[0]);
          const lat = Number(coord[1]);
          if (isValidLng(lng) && isValidLat(lat)) {
            allPoints.push([lng, lat]);
          }
        }
      });
    }
  });

  if (allPoints.length > 0) {
    const lons = allPoints.map(p => p[0]);
    const lats = allPoints.map(p => p[1]);
    const minLng = Math.min(...lons);
    const minLat = Math.min(...lats);
    const maxLng = Math.max(...lons);
    const maxLat = Math.max(...lats);

    if (areBoundsValid(minLng, minLat, maxLng, maxLat)) {
      const bounds: [[number, number], [number, number]] = [
        [minLng, minLat],
        [maxLng, maxLat],
      ];
      mapRef.current.fitBounds(bounds, { padding: 80, duration: 1000 });
      setHasFitBounds(true);
    }
  }
  }, [dwellLocations, routes, heatmapData, daytimeHeatmapData, nighttimeHeatmapData, deviceTracks]);

  // Fit bounds when map loads and data is available
  useEffect(() => {
    if (mapLoaded && !hasFitBounds && (dwellLocations.length > 0 || routes.length > 0)) {
      // Small delay to ensure map is fully ready
      const timer = setTimeout(fitMapToBounds, 100);
      return () => clearTimeout(timer);
    }
  }, [mapLoaded, hasFitBounds, dwellLocations.length, routes.length, fitMapToBounds]);

  // Also fit bounds when data arrives after map is loaded
  useEffect(() => {
    if (mapLoaded && dwellLocations.length > 0 && !hasFitBounds) {
      fitMapToBounds();
    }
  }, [mapLoaded, dwellLocations, hasFitBounds, fitMapToBounds]);

  // Fit to filtered selection when user selects specific items
  useEffect(() => {
    if (!mapRef.current || !mapLoaded) return;
    if (!selectedDwellId && !selectedRouteId) return;

    const allPoints: [number, number][] = [];

    filteredDwellLocations.forEach(location => {
      const lng = location.center_longitude;
      const lat = location.center_latitude;
      if (isValidLng(lng) && isValidLat(lat)) {
        allPoints.push([lng, lat]);
      }
    });

    filteredRoutes.forEach(route => {
      if (route.path_coordinates && Array.isArray(route.path_coordinates)) {
        route.path_coordinates.forEach(coord => {
          if (coord && coord.length >= 2) {
            const a = Number(coord[0]);
            const b = Number(coord[1]);
            if (isValidLng(a) && isValidLat(b)) {
              allPoints.push([a, b]);
            } else if (isValidLng(b) && isValidLat(a)) {
              allPoints.push([b, a]);
            }
          }
        });
      }
    });

    if (allPoints.length > 0) {
      const lons = allPoints.map(p => p[0]);
      const lats = allPoints.map(p => p[1]);
      const minLng = Math.min(...lons);
      const minLat = Math.min(...lats);
      const maxLng = Math.max(...lons);
      const maxLat = Math.max(...lats);

      if (areBoundsValid(minLng, minLat, maxLng, maxLat)) {
        const bounds: [[number, number], [number, number]] = [
          [minLng, minLat],
          [maxLng, maxLat],
        ];
        mapRef.current.fitBounds(bounds, { padding: 50, duration: 500 });
      }
    }
  }, [selectedDwellId, selectedRouteId, filteredDwellLocations, filteredRoutes, mapLoaded]);

  // Add geocoder on map load
  const handleMapLoad = useCallback(() => {
    setMapLoaded(true);
    const map = mapRef.current?.getMap();
    if (map && !geocoderRef.current) {
      // Use data center for geocoder proximity, or US center as fallback
      const proximityCenter = dwellLocations.length > 0
        ? {
            longitude: dwellLocations.reduce((sum, d) => sum + d.center_longitude, 0) / dwellLocations.length,
            latitude: dwellLocations.reduce((sum, d) => sum + d.center_latitude, 0) / dwellLocations.length
          }
        : { longitude: -98.5795, latitude: 39.8283 };
      
      const geocoder = new MapboxGeocoder({
        accessToken: process.env.REACT_APP_MAPBOX_TOKEN || '',
        mapboxgl: require('mapbox-gl') as any,
        marker: false,
        placeholder: 'Search for address or place...',
        proximity: proximityCenter as any,
      });

      map.addControl(geocoder, 'top-left');
      geocoderRef.current = geocoder;
    }
  }, [dwellLocations]);

  // Toggle layer visibility
  const toggleLayer = (layerKey: keyof LayerState) => {
    setLayers(prev => ({
      ...prev,
      [layerKey]: !prev[layerKey]
    }));
  };

  // Categorize routes by frequency
  const getRoutesByFrequency = (minFrequency: number) => {
    return filteredRoutes.filter(route => (route.frequency || 0) >= minFrequency);
  };

  const highFreqRoutes = getRoutesByFrequency(10);
  const medFreqRoutes = filteredRoutes.filter(r => (r.frequency || 0) >= 5 && (r.frequency || 0) < 10);
  const lowFreqRoutes = filteredRoutes.filter(r => (r.frequency || 0) < 5);

  // Create GeoJSON for routes
  const routesGeoJSON = useMemo(() => {
    const features: any[] = [];

    const routesToShow = [
      ...(layers.highFreqRoutes ? highFreqRoutes : []),
      ...(layers.medFreqRoutes ? medFreqRoutes : []),
      ...(layers.lowFreqRoutes ? lowFreqRoutes : [])
    ];

    routesToShow.forEach(route => {
      if (route.path_coordinates && route.path_coordinates.length > 0) {
        features.push({
          type: 'Feature' as const,
          geometry: {
            type: 'LineString' as const,
            coordinates: route.path_coordinates.map(coord => [coord[1], coord[0]]) // [lon, lat]
          },
          properties: {
            id: route.id,
            frequency: route.frequency || 0,
            color: (route.frequency || 0) >= 10 ? '#E74C3C' : (route.frequency || 0) >= 5 ? '#F39C12' : '#3498DB'
          }
        });
      }
    });

    return { type: 'FeatureCollection' as const, features };
  }, [layers, highFreqRoutes, medFreqRoutes, lowFreqRoutes]);

  // Create GeoJSON for device tracks
  const deviceTracksGeoJSON = useMemo(() => {
    if (!layers.allDeviceTracks) return { type: 'FeatureCollection' as const, features: [] };

    const features: any[] = [];

    Object.entries(deviceTracks).forEach(([deviceId, track]) => {
      if (track.coordinates && track.coordinates.length > 0) {
        
        features.push({
          type: 'Feature' as const,
          geometry: {
            type: 'LineString' as const,
            // Convert from [lat, lon] to [lon, lat] for GeoJSON
            coordinates: track.coordinates.map((coord: number[]) => [coord[1], coord[0]])
          },
          properties: {
            deviceId,
            deviceType: track.device_type,
            pointCount: track.point_count
          }
        });
      }
    });

    return { type: 'FeatureCollection' as const, features };
  }, [deviceTracks, layers.allDeviceTracks]);

  // Create GeoJSON for heatmap
  const heatmapGeoJSON = useMemo(() => {
    if (!layers.heatmap || heatmapData.length === 0) {
      return { type: 'FeatureCollection' as const, features: [] };
    }

    const features = heatmapData.map(point => ({
      type: 'Feature' as const,
      geometry: {
        type: 'Point' as const,
        coordinates: [point.lon, point.lat]
      },
      properties: {
        intensity: point.intensity
      }
    }));

    return { type: 'FeatureCollection' as const, features };
  }, [heatmapData, layers.heatmap]);

  // Create GeoJSON for daytime activity
  const daytimeHeatmapGeoJSON = useMemo(() => {
    if (!layers.daytimeActivity || daytimeHeatmapData.length === 0) {
      return { type: 'FeatureCollection' as const, features: [] };
    }

    const features = daytimeHeatmapData.map(point => ({
      type: 'Feature' as const,
      geometry: {
        type: 'Point' as const,
        coordinates: [point.lon, point.lat]
      },
      properties: {
        intensity: point.intensity
      }
    }));

    if (features.length > 0) {
    }

    return { type: 'FeatureCollection' as const, features };
  }, [daytimeHeatmapData, layers.daytimeActivity]);

  // Create GeoJSON for nighttime activity
  const nighttimeHeatmapGeoJSON = useMemo(() => {
    if (!layers.nighttimeActivity || nighttimeHeatmapData.length === 0) {
      return { type: 'FeatureCollection' as const, features: [] };
    }

    const features = nighttimeHeatmapData.map(point => ({
      type: 'Feature' as const,
      geometry: {
        type: 'Point' as const,
        coordinates: [point.lon, point.lat]
      },
      properties: {
        intensity: point.intensity
      }
    }));

    if (features.length > 0) {
    }

    return { type: 'FeatureCollection' as const, features };
  }, [nighttimeHeatmapData, layers.nighttimeActivity]);

  // Get dwell icon color and size based on visits
  const getDwellIconStyle = (visits: number) => {
    if (visits > 20) return { color: '#E74C3C', size: 32 };
    if (visits > 10) return { color: '#F39C12', size: 28 };
    if (visits > 5) return { color: '#3498DB', size: 24 };
    return { color: '#95A5A6', size: 20 };
  };

  const resolvedMapStyle = useMemo(() => {
    const styleMap: Record<MapStyleType, string> = {
      'streets': 'mapbox://styles/mapbox/streets-v12',
      'satellite': 'mapbox://styles/mapbox/satellite-v9',
      'satellite-streets': 'mapbox://styles/mapbox/satellite-streets-v12',
      'light': 'mapbox://styles/mapbox/light-v11',
      'dark': 'mapbox://styles/mapbox/dark-v11',
      'outdoors': 'mapbox://styles/mapbox/outdoors-v12',
    };
    return styleMap[mapStyle];
  }, [mapStyle]);

  return (
    <Box id="enhanced-analysis-map-container" sx={{ height: '600px', position: 'relative' }}>
      {/* Map Container */}
      <Card sx={{ height: '100%', width: '100%' }}>
        <Box sx={{ height: '100%', position: 'relative' }}>
          <Map
            ref={mapRef}
            mapboxAccessToken={process.env.REACT_APP_MAPBOX_TOKEN}
            longitude={viewport.longitude}
            latitude={viewport.latitude}
            zoom={viewport.zoom}
            onMove={(evt) => setViewport(evt.viewState)}
            mapStyle={resolvedMapStyle}
            style={{ width: '100%', height: '100%', minHeight: '600px' }}
            scrollZoom={scrollWheelZoom}
            onLoad={handleMapLoad}
            preserveDrawingBuffer={true}
          >
            {/* Navigation Controls */}
            <NavigationControl position="top-right" />
            <ScaleControl position="bottom-left" />

            {/* Map Style Control */}
            <Box sx={{ position: 'absolute', top: 10, right: 50, zIndex: 1 }}>
              <MapStyleControl
                selectedMapStyle={mapStyle}
                onMapStyleChange={setMapStyle}
              />
            </Box>

            {/* Layer Controls Panel - Right Side Overlay */}
            <Paper
              className="map-layer-controls"
              sx={{
                position: 'absolute',
                top: 60,
                right: 10,
                width: 300,
                maxHeight: 'calc(100% - 120px)',
                overflow: 'auto',
                zIndex: 1,
                bgcolor: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                boxShadow: 3
              }}
            >
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1" fontWeight="bold">Map Controls & Layers</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {/* Scroll Wheel Zoom */}
                    <FormControlLabel
                      control={
                        <Switch
                          checked={scrollWheelZoom}
                          onChange={(e) => setScrollWheelZoom(e.target.checked)}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <MouseIcon fontSize="small" />
                          <Typography variant="body2">Enable Scroll Wheel Zoom</Typography>
                        </Box>
                      }
                    />

                    <Divider />

                    {/* Layer Toggles */}
                    <Typography variant="subtitle2" fontWeight="bold">Data Layers</Typography>
                    <FormGroup>
                      <FormControlLabel
                        control={<Switch checked={layers.dwellLocations} onChange={() => toggleLayer('dwellLocations')} />}
                        label={<Typography variant="body2">Dwell Locations</Typography>}
                      />
                      <FormControlLabel
                        control={<Switch checked={layers.heatmap} onChange={() => toggleLayer('heatmap')} />}
                        label={<Typography variant="body2">Activity Heatmap</Typography>}
                      />
                      <FormControlLabel
                        control={<Switch checked={layers.allDeviceTracks} onChange={() => toggleLayer('allDeviceTracks')} />}
                        label={<Typography variant="body2">Device Tracks</Typography>}
                      />
                    </FormGroup>

                    <Divider />

                    <Typography variant="subtitle2" fontWeight="bold">Route Frequency</Typography>
                    <FormGroup>
                      <FormControlLabel
                        control={<Switch checked={layers.highFreqRoutes} onChange={() => toggleLayer('highFreqRoutes')} />}
                        label={<Typography variant="body2">High Frequency (10+) - {highFreqRoutes.length} routes</Typography>}
                      />
                      <FormControlLabel
                        control={<Switch checked={layers.medFreqRoutes} onChange={() => toggleLayer('medFreqRoutes')} />}
                        label={<Typography variant="body2">Medium Frequency (5-9) - {medFreqRoutes.length} routes</Typography>}
                      />
                      <FormControlLabel
                        control={<Switch checked={layers.lowFreqRoutes} onChange={() => toggleLayer('lowFreqRoutes')} />}
                        label={<Typography variant="body2">Low Frequency (&lt;5) - {lowFreqRoutes.length} routes</Typography>}
                      />
                    </FormGroup>

                    {temporalBreakdown && (
                      <>
                        <Divider />
                        <Typography variant="subtitle2" fontWeight="bold">Temporal Analysis</Typography>
                        <FormGroup>
                          <FormControlLabel
                            control={<Switch checked={layers.daytimeActivity} onChange={() => toggleLayer('daytimeActivity')} />}
                            label={<Typography variant="body2">Daytime Activity ({temporalBreakdown.daytime_points} points)</Typography>}
                          />
                          <FormControlLabel
                            control={<Switch checked={layers.nighttimeActivity} onChange={() => toggleLayer('nighttimeActivity')} />}
                            label={<Typography variant="body2">Nighttime Activity ({temporalBreakdown.nighttime_points} points)</Typography>}
                          />
                        </FormGroup>
                      </>
                    )}
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Paper>

            {/* Heatmap Layer */}
            {layers.heatmap && heatmapGeoJSON.features.length > 0 && (
              <Source id="heatmap-source" type="geojson" data={heatmapGeoJSON}>
                <Layer
                  id="heatmap-layer"
                  type="heatmap"
                  paint={{
                    'heatmap-weight': ['get', 'intensity'],
                    'heatmap-intensity': 1,
                    'heatmap-color': [
                      'interpolate',
                      ['linear'],
                      ['heatmap-density'],
                      0, 'rgba(33,102,172,0)',
                      0.2, 'rgb(103,169,207)',
                      0.4, 'rgb(209,229,240)',
                      0.6, 'rgb(253,219,199)',
                      0.8, 'rgb(239,138,98)',
                      1, 'rgb(178,24,43)'
                    ],
                    'heatmap-radius': 20,
                    'heatmap-opacity': 0.7
                  }}
                />
              </Source>
            )}

            {/* Daytime Activity Heatmap Layer */}
            {layers.daytimeActivity && daytimeHeatmapGeoJSON.features.length > 0 && (
              <Source id="daytime-heatmap-source" type="geojson" data={daytimeHeatmapGeoJSON}>
                <Layer
                  id="daytime-heatmap-layer"
                  type="heatmap"
                  paint={{
                    'heatmap-weight': ['get', 'intensity'],
                    'heatmap-intensity': 1,
                    'heatmap-color': [
                      'interpolate',
                      ['linear'],
                      ['heatmap-density'],
                      0, 'rgba(255,255,0,0)',
                      0.2, 'rgba(255,220,0,0.4)',
                      0.4, 'rgba(255,195,0,0.6)',
                      0.6, 'rgba(255,165,0,0.7)',
                      0.8, 'rgba(255,140,0,0.8)',
                      1, 'rgba(255,100,0,0.9)'
                    ],
                    'heatmap-radius': 20,
                    'heatmap-opacity': 0.7
                  }}
                />
              </Source>
            )}

            {/* Nighttime Activity Heatmap Layer */}
            {layers.nighttimeActivity && nighttimeHeatmapGeoJSON.features.length > 0 && (
              <Source id="nighttime-heatmap-source" type="geojson" data={nighttimeHeatmapGeoJSON}>
                <Layer
                  id="nighttime-heatmap-layer"
                  type="heatmap"
                  paint={{
                    'heatmap-weight': ['get', 'intensity'],
                    'heatmap-intensity': 1,
                    'heatmap-color': [
                      'interpolate',
                      ['linear'],
                      ['heatmap-density'],
                      0, 'rgba(0,0,139,0)',
                      0.2, 'rgba(25,25,170,0.4)',
                      0.4, 'rgba(65,105,225,0.6)',
                      0.6, 'rgba(100,149,237,0.7)',
                      0.8, 'rgba(135,206,250,0.8)',
                      1, 'rgba(173,216,230,0.9)'
                    ],
                    'heatmap-radius': 20,
                    'heatmap-opacity': 0.7
                  }}
                />
              </Source>
            )}

            {/* Route Lines */}
            {routesGeoJSON.features.length > 0 && (
              <Source id="routes-source" type="geojson" data={routesGeoJSON}>
                <Layer
                  id="routes-layer"
                  type="line"
                  paint={{
                    'line-color': ['get', 'color'],
                    'line-width': 3,
                    'line-opacity': 0.7
                  }}
                />
              </Source>
            )}

            {/* Device Tracks */}
            {deviceTracksGeoJSON.features.length > 0 && (
              <Source id="device-tracks-source" type="geojson" data={deviceTracksGeoJSON}>
                <Layer
                  id="device-tracks-layer"
                  type="line"
                  paint={{
                    'line-color': '#9B59B6',
                    'line-width': 2,
                    'line-opacity': 0.6
                  }}
                />
              </Source>
            )}

            {/* Dwell Location Markers */}
            {layers.dwellLocations && filteredDwellLocations.map((location) => {
              const { color, size } = getDwellIconStyle(location.total_visits || 0);
              
              return (
                <Marker
                  key={location.id}
                  longitude={location.center_longitude}
                  latitude={location.center_latitude}
                  onClick={(e) => {
                    e.originalEvent.stopPropagation();
                    setSelectedDwell(location);
                  }}
                >
                  <div
                    style={{
                      backgroundColor: color,
                      border: '3px solid white',
                      borderRadius: '50%',
                      width: size,
                      height: size,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: size > 25 ? '12px' : '10px',
                      fontWeight: 'bold',
                      boxShadow: '0 3px 6px rgba(0,0,0,0.4)',
                      cursor: 'pointer'
                    }}
                  >
                    {location.total_visits || 0}
                  </div>
                </Marker>
              );
            })}

            {/* Dwell Location Popup */}
            {selectedDwell && (
              <Popup
                longitude={selectedDwell.center_longitude}
                latitude={selectedDwell.center_latitude}
                onClose={() => setSelectedDwell(null)}
                closeOnClick={false}
              >
                <Box sx={{ p: 1 }}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {selectedDwell.place_name || 'Unknown Location'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Visits:</strong> {selectedDwell.total_visits || 0}
                  </Typography>
                  {selectedDwell.total_duration && (
                    <Typography variant="body2">
                      <strong>Total Duration:</strong> {(selectedDwell.total_duration / 60).toFixed(1)} hours
                    </Typography>
                  )}
                  {selectedDwell.address && (
                    <Typography variant="body2" color="text.secondary">
                      {selectedDwell.address}
                    </Typography>
                  )}
                </Box>
              </Popup>
            )}
          </Map>
        </Box>
      </Card>
    </Box>
  );
};

export default EnhancedMapVisualizationGL;
